import env from "@/config/env";
import { ApiError } from "@/types/api";

const DEFAULT_TIMEOUT_MS = 30_000;

const AUTH_ENDPOINTS = [
  "/auth/refresh",
  "/auth/login",
  "/auth/logout",
  "/auth/me",
  "/auth/mfa/verify",
  "/auth/mfa/resend",
];

class ApiClient {
  private readonly baseUrl: string;
  private onUnauthorized?: () => void;
  private refreshPromise: Promise<void> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  private async rawFetch(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;

    const isFormData = options.body instanceof FormData;

    const headers: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string>),
    };

    // Include CSRF token for mutating requests
    if (
      options.method &&
      options.method !== "GET" &&
      options.method !== "HEAD"
    ) {
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      }
    }

    const response = await fetch(url, {
      ...options,
      credentials: "include",
      headers,
      signal: options.signal ?? AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const body = errorBody as Record<string, unknown>;
      throw new ApiError(
        (body.detail as string) ?? (body.message as string) ?? "Wystąpił błąd",
        response.status,
        body.code as string | undefined,
        body.errors as Record<string, string[]> | undefined,
      );
    }

    return response;
  }

  private async rawRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await this.rawFetch(endpoint, options);

    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }

  private refreshToken(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.rawRequest<void>("/auth/refresh", {
      method: "POST",
    }).finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    try {
      return await this.rawRequest<T>(endpoint, options);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.statusCode === 401 &&
        !AUTH_ENDPOINTS.some((path) => endpoint.startsWith(path))
      ) {
        try {
          await this.refreshToken();
          return await this.rawRequest<T>(endpoint, options);
        } catch {
          this.onUnauthorized?.();
          throw error;
        }
      }
      throw error;
    }
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: formData,
    });
  }

  async getBlob(endpoint: string): Promise<{ blob: Blob; response: Response }> {
    const options: RequestInit = { method: "GET" };
    try {
      const response = await this.rawFetch(endpoint, options);
      const blob = await response.blob();
      return { blob, response };
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.statusCode === 401 &&
        !AUTH_ENDPOINTS.some((path) => endpoint.startsWith(path))
      ) {
        try {
          await this.refreshToken();
          const response = await this.rawFetch(endpoint, options);
          const blob = await response.blob();
          return { blob, response };
        } catch {
          this.onUnauthorized?.();
          throw error;
        }
      }
      throw error;
    }
  }
}

function getCsrfTokenFromCookie(): string | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith("AdaraPanel.Csrf="));
  return match
    ? decodeURIComponent(match.substring(match.indexOf("=") + 1))
    : null;
}

export const apiClient = new ApiClient(env.apiBaseUrl);
